import { useDrop } from "react-dnd";

import { calculateImageBackgroundPosition, calculateImageBackgroundSize } from "../../utils";
import { DRAGGABLE_SLICE } from "../../widget.constants";
import { usePuzzle } from "../../widget.state";
import { PuzzleRoundStatusEnum, PuzzleRoundType } from "../../widget.types";
import { PuzzleSlice } from "../puzzle-slice/puzzle-slice.component";

export function PuzzleSlices({
  slicesPerRow,
  totalRows,
  pendingSlices,
  imageSlices,
  puzzleImage,
  puzzleOriginalImageWidth,
  puzzleOriginalImageHeight,
  imageWidth,
  imageHeight,
  sliceWidth,
  sliceHeight,
  scaleFactor,
  status,
  setData,
  onStart,
}: {
  slicesPerRow: number;
  totalRows: number;
  pendingSlices: PuzzleRoundType["pendingSlices"];
  imageSlices: { x: number; y: number }[];
  puzzleImage: string;
  puzzleOriginalImageWidth: number;
  puzzleOriginalImageHeight: number;
  imageWidth: number;
  imageHeight: number;
  sliceWidth: number;
  sliceHeight: number;
  scaleFactor: number;
  status: PuzzleRoundStatusEnum;
  setData: ReturnType<typeof usePuzzle>["setData"];
  onStart: () => void;
}) {
  return (
    <div
      className="arcade__puzzle__slices"
      style={{
        gridTemplateColumns: `repeat(${slicesPerRow}, 1fr)`,
        width: imageWidth * scaleFactor + (slicesPerRow - 1) * (sliceWidth * 0.08),
        height: imageHeight * scaleFactor + (totalRows - 1) * (sliceHeight * 0.08),
      }}
    >
      {pendingSlices.map((sliceIndex, pendingIndex) => (
        <PuzzleSlicesSlot
          key={`pendingSlice-${pendingIndex}`}
          isDroppable={sliceIndex === null}
          targetPendingIndex={pendingIndex}
          width={sliceWidth * scaleFactor}
          height={sliceHeight * scaleFactor}
        >
          {sliceIndex !== null ? (
            <PuzzleSlice
              isDraggable={true}
              sliceIndex={sliceIndex}
              pendingIndex={pendingIndex}
              backgroundSize={calculateImageBackgroundSize(
                puzzleOriginalImageWidth,
                puzzleOriginalImageHeight,
                imageWidth,
                imageHeight,
                scaleFactor
              )}
              backgroundImage={`url(${puzzleImage})`}
              backgroundPosition={calculateImageBackgroundPosition(
                puzzleOriginalImageWidth,
                puzzleOriginalImageHeight,
                imageWidth,
                imageHeight,
                imageSlices[sliceIndex],
                scaleFactor
              )}
              status={status}
              setData={setData}
              onStart={onStart}
            />
          ) : null}
        </PuzzleSlicesSlot>
      ))}
    </div>
  );
}

function PuzzleSlicesSlot({
  isDroppable,
  targetPendingIndex,
  width,
  height,
  children,
}: {
  isDroppable: boolean;
  targetPendingIndex: number;
  width: number;
  height: number;
  children: React.ReactNode;
}) {
  const [{ canDrop, isHovered }, dropRef] = useDrop(
    () => ({
      canDrop: () => isDroppable,
      accept: DRAGGABLE_SLICE,
      drop: () => ({ targetPendingIndex }),
      collect: (monitor) => ({
        canDrop: monitor.canDrop(),
        isHovered: monitor.isOver(),
      }),
    }),
    [isDroppable, targetPendingIndex]
  );

  const updatePuzzleSlicesSlotStyleOnStateChange = (): string =>
    [
      "arcade__puzzle__slices__slot",
      canDrop && "arcade__puzzle__slices__slot--droppable",
      canDrop && isHovered && "arcade__puzzle__slices__slot--hovered",
    ]
      .filter(Boolean)
      .join(" ");

  return (
    <div
      ref={dropRef}
      className={updatePuzzleSlicesSlotStyleOnStateChange()}
      style={{ width, height }}
    >
      {children}
    </div>
  );
}
